import type { HealthInterestOption } from "../data/healthInterestOptions";

const PUBLIC_IMG = (file: string) =>
  `${process.env.PUBLIC_URL || ""}/health-interests/${file}`;

/** Local fallback images when API `goalIcon` is missing or fails to load. */
const GOAL_TYPE_ASSETS: Record<
  string,
  { file: string; imagePosition?: string; fallbackInfo: string }
> = {
  FITNESS_AND_ACTIVITY: {
    file: "daily-exercise.jpg",
    imagePosition: "center 50%",
    fallbackInfo:
      "Workout plans, activity reminders, and tips to help you stay active every day.",
  },
  GET_FIT_AND_MOVE_MORE: {
    file: "daily-exercise.jpg",
    imagePosition: "center 50%",
    fallbackInfo:
      "Workout plans, activity reminders, and tips to help you stay active every day.",
  },
  NUTRITION_AND_DIET: {
    file: "healthy-eating.jpg",
    imagePosition: "center 45%",
    fallbackInfo:
      "Meal ideas, nutrition guidance, and diet support for balanced eating.",
  },
  EAT_AND_NOURISH_BETTER: {
    file: "healthy-eating.jpg",
    imagePosition: "center 45%",
    fallbackInfo:
      "Meal ideas, nutrition guidance, and diet support for balanced eating.",
  },
  MENTAL_WELLNESS: {
    file: "peace-of-mind.jpg",
    imagePosition: "center 35%",
    fallbackInfo:
      "Meditation, breathing exercises, and stress relief for mental wellness.",
  },
  MIND_STRESS_AND_SLEEP: {
    file: "peace-of-mind.jpg",
    imagePosition: "center 35%",
    fallbackInfo:
      "Meditation, breathing exercises, and stress relief for mental wellness.",
  },
  HEALTH_AWARENESS: {
    file: "live-health-insights.jpg",
    imagePosition: "center 45%",
    fallbackInfo:
      "Track trends, vitals, and insights so you understand your health better.",
  },
  UNDERSTAND_MY_HEALTH: {
    file: "live-health-insights.jpg",
    imagePosition: "center 45%",
    fallbackInfo:
      "Track trends, vitals, and insights so you understand your health better.",
  },
  CONDITION_MANAGEMENT: {
    file: "doctor-checkups.jpg",
    imagePosition: "center 25%",
    fallbackInfo:
      "Doctor visits, medicine reminders, and tools to manage ongoing conditions.",
  },
  MANAGE_A_CONDITION: {
    file: "doctor-checkups.jpg",
    imagePosition: "center 25%",
    fallbackInfo:
      "Doctor visits, medicine reminders, and tools to manage ongoing conditions.",
  },
  LIFE_STAGE_AND_FAMILY: {
    file: "health-improvement.jpg",
    imagePosition: "center 45%",
    fallbackInfo:
      "Wellness goals and support for family health through every life stage.",
  },
};

export const getLocalGoalImageUrl = (goalType: string): string => {
  const asset = GOAL_TYPE_ASSETS[goalType.toUpperCase()];
  return asset ? PUBLIC_IMG(asset.file) : PUBLIC_IMG("daily-exercise.jpg");
};

const ROOT_ARRAY_KEYS = [
  "data",
  "categories",
  "healthInterestCategories",
  "health_interest_categories",
  "content",
  "items",
  "result",
  "body",
  "payload",
  "records",
];

const toTrimmedString = (value: unknown): string => {
  if (value == null) return "";
  if (typeof value === "string") return value.trim();
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value).trim();
  }
  return "";
};

const pickString = (row: Record<string, unknown>, keys: string[]): string => {
  for (const key of keys) {
    const value = row[key];
    const trimmed = toTrimmedString(value);
    if (trimmed !== "") return trimmed;
  }
  return "";
};

const isInactive = (row: Record<string, unknown>): boolean => {
  if (row.isActive === false || row.active === false || row.enabled === false) {
    return true;
  }
  const status = toTrimmedString(row.status).toLowerCase();
  return status === "inactive" || status === "disabled" || status === "0";
};

/** Walk common API envelopes until a category array is found. */
export const extractCategoriesArray = (
  response: unknown,
  depth = 0
): unknown[] => {
  if (!response || depth > 4) return [];
  if (Array.isArray(response)) return response;

  const root = response as Record<string, unknown>;

  for (const key of ROOT_ARRAY_KEYS) {
    const value = root[key];
    if (Array.isArray(value)) return value;
    if (value && typeof value === "object") {
      const nested = extractCategoriesArray(value, depth + 1);
      if (nested.length > 0) return nested;
    }
  }

  return [];
};

const resolveAssetBase = (): string => {
  const v1 = process.env.REACT_APP_BASE_URL_V1 || "";
  if (!v1) return "";
  return v1.replace(/\/api\/v1\/?$/i, "").replace(/\/$/, "");
};

const resolveRemoteImageUrl = (raw: unknown): string => {
  const url = raw == null ? "" : toTrimmedString(raw);
  if (!url) return "";
  if (/^https?:\/\//i.test(url) || url.startsWith("data:")) return url;

  const base = resolveAssetBase();
  if (url.startsWith("/")) return base ? `${base}${url}` : url;
  return base ? `${base}/${url.replace(/^\//, "")}` : url;
};

const resolveCategoryImage = (
  row: Record<string, unknown>,
  goalType: string
): { imageUrl: string; imagePosition?: string } => {
  const normalizedGoal = goalType.toUpperCase();
  const localAsset = GOAL_TYPE_ASSETS[normalizedGoal];

  const fromApi = resolveRemoteImageUrl(
    row.goalIcon ??
      row.goal_icon ??
      row.imageUrl ??
      row.image_url ??
      row.goalImageUrl ??
      row.goalImage ??
      row.iconUrl ??
      row.icon_url ??
      row.thumbnailUrl ??
      row.imagePath ??
      row.image ??
      row.icon
  );

  if (fromApi) {
    return {
      imageUrl: fromApi,
      imagePosition:
        typeof row.imagePosition === "string"
          ? row.imagePosition
          : localAsset?.imagePosition,
    };
  }

  if (localAsset) {
    return {
      imageUrl: PUBLIC_IMG(localAsset.file),
      imagePosition: localAsset.imagePosition,
    };
  }

  return { imageUrl: PUBLIC_IMG("daily-exercise.jpg") };
};

type MappedCategory = HealthInterestOption & { sortOrder: number };

const mapCategoryItem = (item: unknown, index: number): MappedCategory | null => {
  if (!item || typeof item !== "object") return null;

  const row = item as Record<string, unknown>;
  if (isInactive(row)) return null;

  const goalType = pickString(row, ["goalType", "goal_type", "type", "code"]);
  const fallbackLabel = pickString(row, [
    "goalTitle",
    "goal_title",
    "name",
    "title",
    "label",
    "categoryName",
    "displayName",
  ]);

  if (!fallbackLabel) return null;

  const asset = GOAL_TYPE_ASSETS[goalType.toUpperCase()];
  const fallbackInfo =
    pickString(row, [
      "goalDescription",
      "goal_description",
      "description",
      "info",
      "longDescription",
      "shortDescription",
      "summary",
      "about",
    ]) || asset?.fallbackInfo || "";

  const id =
    toTrimmedString(
      row.id ?? row.categoryId ?? row.healthInterestCategoryId ?? index
    ) || String(index);

  const sortOrder = Number(
    row.sortOrder ?? row.displayOrder ?? row.sequence ?? row.order ?? index
  );

  const { imageUrl, imagePosition } = resolveCategoryImage(row, goalType);

  return {
    id,
    labelKey: goalType || id,
    fallbackLabel,
    fallbackInfo,
    imageUrl,
    imagePosition,
    sortOrder: Number.isFinite(sortOrder) ? sortOrder : index,
  };
};

/** Maps GET /health-interests/categories `{ success, data: [{ id, goalType, goalTitle, ... }] }`. */
export const mapHealthInterestCategoriesResponse = (
  response: unknown
): HealthInterestOption[] => {
  return extractCategoriesArray(response)
    .map((item, index) => mapCategoryItem(item, index))
    .filter((item): item is MappedCategory => item != null)
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map(({ sortOrder: _sortOrder, ...option }) => option);
};
